import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import { getUserById, updateUser, getUserPracticeById } from "../../api";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import "../../custom.scss";

export function EditUser(props) {
  const { userId, onHide, show, onUserUpdate } = props;

  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [availablePractices, setAvailablePractices] = useState([]);
  const [loggedInRoles, setLoggedInRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    occupation: "",
    role: [],
    practice: [],
  });

  useEffect(() => {
    async function loadUserAndPractice() {
      try {
        if (!userId) {
          console.error("No user ID provided.");
          return;
        }

        const token = sessionStorage.getItem("user");
        let availablePracs = [];
        if (token) {
          const decoded = jwtDecode(token);
          setLoggedInRoles(decoded.role);
          const loggedInUserPractices = await getUserPracticeById(decoded._id);
          availablePracs = loggedInUserPractices.map((practice) => ({
            practiceNumber: practice.practiceNumber,
            practiceName: practice.practiceName,
          }));
          setAvailablePractices(availablePracs);
        }
        
        const userData = await getUserById(userId);
        setUser(userData);
        setSelectedRoles(userData.role || []);
        setSelectedPractices(userData.practice || []);
      } catch (e) {
        console.error("Failed to load user or practices:", e);
      }
    }
    loadUserAndPractice();
  }, [userId]);

  const validateForm = () => {
    const newErrors = {};
    const isValidEmailFormat = /\S+@\S+\.\S+/.test(user.email);

    if (!user.email || !isValidEmailFormat) newErrors.email = "Enter a valid email.";
    if (!user.firstName) newErrors.firstName = "First name is required.";
    if (!user.lastName) newErrors.lastName = "Last name is required.";
    if (!user.occupation) newErrors.occupation = "Occupation is required.";
    if (!user.phoneNumber || !/^\d{3}-\d{3}-\d{4}$/.test(user.phoneNumber))
      newErrors.phoneNumber = "Enter a valid phone number (xxx-xxx-xxxx).";
    if (!selectedRoles.length) newErrors.role = "At least one role must be selected.";
    if (!selectedPractices.length) newErrors.practice = "At least one practice must be selected.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await updateUser(userId, { ...user, role: selectedRoles, practice: selectedPractices });
      if (response.status === 200) {
        console.log("User updated successfully:", response);
        setSuccessAlert(true);
        setTimeout(() => {
          setSuccessAlert(false);
          props.onHide();
          onUserUpdate(); // ✅ Call the parent component's update function
        }, 1000);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        setErrors((prev) => ({
          ...prev,
          email: error.response.data?.error || "Email already in use.",
        }));
        return;
      }
      console.error("Failed to update user:", error);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "");
      const formatted = digits.slice(0, 10).replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3");
      setUser((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setUser((prev) => ({ ...prev, [name]: value }));
    }

    // Clear stale field-level error (e.g. the 409 email message) so the user
    // gets fresh feedback on the next submit.
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  const handleRoleChange = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber]
    );
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    props.onHide();
  };

  const isSuperAdmin = loggedInRoles.includes("SuperAdmin"); // ✅ Check if the current user is a Super Admin
  const isSystemAdmin = loggedInRoles.includes("SystemAdmin"); // SystemAdmin-only features (e.g. SyncTo)

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton={!closeAlert && !successAlert} className="border-secondary">
        <div className="w-100">
        <Modal.Title id="contained-modal-title-vcenter" className="text-primary">
          Edit User
          
        </Modal.Title>
        </div>
      </Modal.Header>
      {closeAlert && (
            <Alert
              variant="info"
              className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            >
              <span>Are you sure you want to close? Changes will not be saved.</span>
              <div className="ms-auto d-flex">
              <Button variant="outline-primary" className="btn btn-outline-primary me-1"  onClick={handleCancelYes}>
                <FaCheck />
                </Button>
                <Button variant="outline-secondary" className="btn btn-outline-secondary me-1"  onClick={handleCancelNo}>
                <FaTimes />
                </Button>
              </div>
            </Alert>
          )}
          {successAlert && (
            <Alert
              variant="success"
              className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal"
            >
              <span>User updated successfully!</span>
            </Alert>
          )}
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                name="email"
                value={user.email}
                isInvalid={!!errors.email}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="userFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                name="firstName"
                value={user.firstName}
                isInvalid={!!errors.firstName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="userLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                name="lastName"
                value={user.lastName}
                isInvalid={!!errors.lastName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="userHPCSA">
              <Form.Label>HPCSA#</Form.Label>
              <Form.Control
                name="hpcsa"
                value={user.hpcsa}

                onChange={handleChange}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="userPhoneNumber">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                placeholder="xxx-xxx-xxxx"
                name="phoneNumber"
                value={user.phoneNumber}
                isInvalid={!!errors.phoneNumber}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="formGridOccupation">
              <Form.Label>Occupation</Form.Label>
              <Form.Select
                name="occupation"
                value={user.occupation || ""}
                isInvalid={!!errors.occupation}
                onChange={handleChange}
              >
                <option value="">Choose...</option>
                <option>Administration</option>
                <option>Doctor</option>
                <option>Finance</option>
                <option>Nurse</option>
                <option>Occupational Therapist</option>
                <option>Podiatrist</option>
                <option>Physio Therapist</option>
                <option>Psychologist</option>
                <option>Speech Therapist</option>
                <option>Reception</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.occupation}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="roleCheckboxes">
              <Form.Label>Roles</Form.Label>
              <div>
                {["Administration", "Practitioner", "Finance", "SuperAdmin"].map((role) => (
                  <Form.Check
                    key={role}
                    type="checkbox"
                    label={role}
                    checked={selectedRoles.includes(role)}
                    onChange={isSuperAdmin ? () => handleRoleChange(role) : undefined} // Disable interaction for non-admins
                    disabled={!isSuperAdmin}
                  />
                ))}
              </div>
              {errors.role && <div className="text-danger">{errors.role}</div>}
            </Form.Group>

            <Form.Group as={Col} controlId="practiceCheckboxes">
              <Form.Label>Practices</Form.Label>
              <div>
                {availablePractices.map((prac) => (
                  <Form.Check
                    key={prac.practiceNumber}
                    type="checkbox"
                    label={prac.practiceName}
                    checked={selectedPractices.includes(prac.practiceNumber)}
                    onChange={isSuperAdmin ? () => handlePracticeChange(prac.practiceNumber) : undefined} // Disable interaction for non-admins
                    disabled={!isSuperAdmin}
                  />
                ))}
              </div>
              {errors.practice && <div className="text-danger">{errors.practice}</div>}
            </Form.Group>

            {/* SystemAdmin-only: enable SyncTo (Azure transcription) for this user.
                Defence-in-depth — backend allowlist also requires SystemAdmin. */}
            {isSystemAdmin && (
              <Form.Group as={Col} controlId="featuresSyncTo">
                <Form.Label>Features</Form.Label>
                <div>
                  <Form.Check
                    type="checkbox"
                    id="feature-syncTo"
                    label="SyncTo (Azure transcription)"
                    checked={!!user?.features?.syncTo}
                    onChange={(e) =>
                      setUser((prev) => ({
                        ...prev,
                        features: {
                          ...(prev.features || {}),
                          syncTo: e.target.checked,
                        },
                      }))
                    }
                  />
                </div>
              </Form.Group>
            )}
          </Row>

          <div className="d-flex justify-content-center align-items-center">
          <Button
            variant="primary"
            className="px-5 py-2" // Adjust padding to make it longer
            onClick={handleSubmit}
            type="submit"
          >
            Submit
          </Button>
        </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
